import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import mintPreviewTokens from '../../../lib/printessMint';
import t from './tokens';

// Post-add upsell for personalised gifts (#5328): the mug, the magnet, the keychain,
// each shown as the PRODUCT with this student's photo already on it -- a real Printess
// render of the template's Mockup document, not a composite of ours.
//
// A Printess product is a SET: the product is "Personalised Gifts", its variants are
// the items, and each item carries its own template (Shop::ProductVariant
// #printess_template_id). Which sets appear here is per product, exactly like the
// add-on step (Shop::ProductUpsellSet).
//
// Cards arrive in one of two states, and the difference is visible on purpose:
//   * a personalised render, once a save token exists for (template, pose);
//   * the STOCK product shot until then, labelled as such, while the hidden editor
//     below mints one. Silently showing the stock photo would read as a preview of
//     somebody else's child.
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const s = {
  page: { background: t.pageBg, minHeight: '100vh', padding: '2.5rem 1.25rem' },
  inner: { maxWidth: 960, margin: '0 auto', textAlign: 'center' },
  h1: {
    fontSize: 'clamp(1.5rem,3vw,2rem)', fontWeight: 700, color: t.ink, margin: '0 0 0.5rem',
  },
  sub: { color: t.muted, margin: '0 0 2rem', fontSize: '1rem' },
  cards: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '1rem', textAlign: 'left',
  },
  card: {
    background: t.surface,
    border: '1px solid rgba(0,0,0,.08)',
    borderRadius: 14,
    boxShadow: t.shadowSm,
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
  },
  shot: {
    width: '100%',
    aspectRatio: '1 / 1',
    objectFit: 'contain',
    background: t.pageBg,
    borderRadius: 10,
    marginBottom: '0.75rem',
  },
  pendingShot: { opacity: 0.55 },
  note: {
    fontSize: '0.7rem', fontWeight: 600, color: t.muted, margin: '0 0 0.4rem', letterSpacing: '0.02em',
  },
  name: { fontWeight: 700, fontSize: '1.05rem', color: t.ink },
  price: {
    fontSize: '1.4rem', fontWeight: 700, color: t.brand, margin: '0.35rem 0 0.75rem',
  },
  select: {
    background: t.brand,
    color: '#fff',
    border: 'none',
    borderRadius: t.radius,
    padding: '0.7rem',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: '0.95rem',
    marginTop: 'auto',
  },
  skip: {
    marginTop: '1.75rem',
    background: 'none',
    border: 'none',
    color: t.muted,
    cursor: 'pointer',
    fontWeight: 600,
    textDecoration: 'underline',
    fontSize: '0.95rem',
  },
  frame: {
    position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none', border: 0,
  },
};

const PersonalizedUpsell = ({
  sets, studentName, pose, studentImageId, printessToken,
  previewsUrl, saveTokenUrl, csrfToken, onSelect, onSkip,
}) => {
  const [previews, setPreviews] = useState([]);
  // The URL Printess imports from. It comes from the previews endpoint rather than
  // the pose already in hand: poses are private on S3 and the catalog's URL is signed
  // for 60 seconds, which expires part-way through a sequential mint queue.
  const [injectUrl, setInjectUrl] = useState(null);
  const frameRef = useRef(null);

  // Every item across the offered sets, in the admin's order. One flat grid: a
  // shopper is choosing a gift, not browsing our catalog structure.
  const items = sets.flatMap((set) => (set.variants || []).map((variant) => ({
    ...variant, productId: set.id, productName: set.name,
  })));

  const load = useCallback(async () => {
    if (!pose?.id || !previewsUrl) return [];

    try {
      const url = `${previewsUrl}?pose_id=${pose.id}&student_image_id=${studentImageId || ''}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      const data = await res.json();
      setPreviews(data.previews || []);
      if (data.pose_url) setInjectUrl(data.pose_url);
      return data.previews || [];
    } catch (e) {
      // A preview is decoration on a product card -- never take the step down for it.
      return [];
    }
  }, [pose, previewsUrl, studentImageId]);

  useEffect(() => { load(); }, [load]);

  // Mint whatever has no token yet, one template at a time, then re-read the
  // previews so the freshly personalised renders replace the stock shots.
  useEffect(() => {
    if (!previews.length || !printessToken || !injectUrl) return undefined;

    const templates = [...new Set(previews.filter((p) => !p.minted).map((p) => p.template_id))];
    if (!templates.length) return undefined;

    return mintPreviewTokens({
      frame: frameRef.current,
      token: printessToken,
      poseUrl: injectUrl,
      poseId: pose.id,
      templates,
      onMinted: (template, saveToken) => fetch(saveTokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({
          template, pose_id: pose.id, student_image_id: studentImageId, save_token: saveToken,
        }),
      }).then(load, load),
    });
    // `previews` is the trigger; load/mint deliberately re-run only when it changes.
  }, [previews, injectUrl, printessToken, pose, saveTokenUrl, csrfToken, studentImageId, load]);

  const previewFor = (item) => previews.find((p) => Number(p.variant_id) === Number(item.id)) || {};

  return (
    <div style={s.page}>
      <div style={s.inner}>
        <h1 style={s.h1}>{`Put ${studentName ? `${studentName}'s` : 'their'} photo on something`}</h1>
        <p style={s.sub}>Personalize it in the next step — you can change the photo there too.</p>

        <div style={s.cards}>
          {items.map((item) => {
            const preview = previewFor(item);
            const personalized = preview.product_url || preview.image_url;
            const shot = personalized || preview.stock_url;

            return (
              <div key={`${item.productId}-${item.id}`} style={s.card}>
                {shot ? (
                  <img
                    src={shot}
                    alt={item.name}
                    style={personalized ? s.shot : { ...s.shot, ...s.pendingShot }}
                  />
                ) : null}
                {shot && !personalized ? (
                  <p style={s.note}>PREPARING YOUR PREVIEW…</p>
                ) : null}
                <span style={s.name}>{item.name}</span>
                <div style={s.price}>{money(item.price)}</div>
                <button type="button" style={s.select} onClick={() => onSelect(item)}>
                  Personalize
                </button>
              </div>
            );
          })}
        </div>

        <button type="button" style={s.skip} onClick={onSkip}>No thanks, continue</button>
      </div>

      {/* The minting rig. Hidden rather than absent: Printess only hands out a save
          token from a real editor, and the token is what carries the student's photo. */}
      <iframe ref={frameRef} title="preview-builder" style={s.frame} />
    </div>
  );
};

PersonalizedUpsell.propTypes = {
  sets: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    variants: PropTypes.arrayOf(PropTypes.object),
  })),
  studentName: PropTypes.string,
  pose: PropTypes.shape({ id: PropTypes.number, watermark_url: PropTypes.string }),
  studentImageId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  printessToken: PropTypes.string,
  previewsUrl: PropTypes.string,
  saveTokenUrl: PropTypes.string,
  csrfToken: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
};

PersonalizedUpsell.defaultProps = {
  sets: [],
  studentName: '',
  pose: null,
  studentImageId: null,
  printessToken: '',
  previewsUrl: '',
  saveTokenUrl: '',
  csrfToken: '',
};

export default PersonalizedUpsell;
